import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge, Button, Group, Kbd, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck, IconFilter, IconX } from '@tabler/icons-react';

import { useIsMobile } from '@/hooks/useIsMobile';
import { analyzeRange } from '@/lib/rangeAnalyzer';
import { parseHandList } from '@/lib/rangeParser';
import { useChartStore } from '@/store/chartStore';

interface RangeFilterModalProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Spotlight-style modal for filtering hands by range notation
 */
export function RangeFilterModal({ opened, onClose }: RangeFilterModalProps) {
  const visibleHands = useChartStore((state) => state.visibleHands);
  const rangeFilterString = useChartStore((state) => state.rangeFilterString);
  const setVisibleHands = useChartStore((state) => state.setVisibleHands);
  const clearHandFilter = useChartStore((state) => state.clearHandFilter);

  // Initialize input from current filter
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue] = useDebouncedValue(inputValue, 150);
  const inputRef = useRef<HTMLInputElement>(null);

  // Pre-populate with existing filter when modal opens and focus input
  useEffect(() => {
    if (opened) {
      setInputValue(rangeFilterString);
      // Focus after a short delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [opened, rangeFilterString]);

  // Parse and analyze the input
  const analysis = useMemo(() => {
    if (!debouncedValue.trim()) {
      return null;
    }
    const hands = parseHandList(debouncedValue);
    return analyzeRange(hands);
  }, [debouncedValue]);

  const handleApply = useCallback(() => {
    if (analysis && analysis.total > 0) {
      setVisibleHands(analysis.hands, inputValue.trim());
      onClose();
    }
  }, [analysis, inputValue, setVisibleHands, onClose]);

  const handleClear = useCallback(() => {
    clearHandFilter();
    setInputValue('');
    onClose();
  }, [clearHandFilter, onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && analysis && analysis.total > 0) {
        event.preventDefault();
        handleApply();
      }
    },
    [analysis, handleApply],
  );

  const isFilterActive = visibleHands !== null;
  const isMobile = useIsMobile();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconFilter size={20} />
          <Text fw={500}>Filter Hands</Text>
          {!isMobile && (
            <Group gap={4}>
              <Kbd>⌘</Kbd>+<Kbd>K</Kbd>
            </Group>
          )}
        </Group>
      }
      centered={!isMobile}
      size="md"
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      styles={isMobile ? { inner: { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' } } : undefined}
    >
      <Stack gap="md">
        <TextInput
          ref={inputRef}
          placeholder="e.g., 22+, AJs+, KQ, 76s-JTs"
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          size="md"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          styles={{
            input: {
              fontFamily: 'monospace',
              fontSize: '1rem',
            },
          }}
        />

        {/* Analysis results */}
        <AnalysisDisplay analysis={analysis} inputValue={inputValue} />

        {/* Interactive examples */}
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Examples:
          </Text>
          {['AA', 'AKs', '77+', 'ATs-AQs', 'K*', '*8'].map((example) => (
            <ExampleChip key={example} example={example} onClick={() => setInputValue(example)} />
          ))}
        </Group>

        {/* Actions */}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            <Kbd>↵</Kbd> to apply
          </Text>
          <Group gap="xs">
            {isFilterActive && (
              <Button variant="subtle" color="gray" size="sm" leftSection={<IconX size={16} />} onClick={handleClear}>
                Clear filter
              </Button>
            )}
            <Button
              size="sm"
              leftSection={<IconCheck size={16} />}
              onClick={handleApply}
              disabled={!analysis || analysis.total === 0}
            >
              Apply
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Clickable example chip
 */
function ExampleChip({ example, onClick }: { example: string; onClick: () => void }) {
  return (
    <Badge
      component="button"
      size="sm"
      variant="light"
      color="blue"
      style={{ cursor: 'pointer', fontFamily: 'monospace', textTransform: 'none' }}
      onClick={onClick}
    >
      {example}
    </Badge>
  );
}

/**
 * Display analysis of the parsed range
 */
function AnalysisDisplay({
  analysis,
  inputValue,
}: {
  analysis: ReturnType<typeof analyzeRange> | null;
  inputValue: string;
}) {
  if (!inputValue.trim()) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xs">
        Enter a range to filter visible hands
      </Text>
    );
  }

  if (!analysis || analysis.total === 0) {
    return (
      <Text size="sm" c="red.5" ta="center" py="xs">
        No valid hands matched
      </Text>
    );
  }

  return (
    <Group gap="xs" justify="center" py="xs">
      <Badge size="lg" variant="light" color="green">
        {analysis.total} hands
      </Badge>
      {analysis.pairs > 0 && (
        <Badge size="sm" variant="outline" color="gray">
          {analysis.pairs} pairs
        </Badge>
      )}
      {analysis.suited > 0 && (
        <Badge size="sm" variant="outline" color="gray">
          {analysis.suited} suited
        </Badge>
      )}
      {analysis.offsuit > 0 && (
        <Badge size="sm" variant="outline" color="gray">
          {analysis.offsuit} offsuit
        </Badge>
      )}
    </Group>
  );
}
